import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/Button';
import { Building2, Calendar, Shield } from 'lucide-react';
export function DemoLogin() {
  const navigate = useNavigate();
  const {
    loginAsDemo
  } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);
  const handleDemoLogin = async (role: 'brand' | 'organizer' | 'super_admin') => {
    setLoading(role);
    try {
      await loginAsDemo(role);
      // Navigate based on role
      if (role === 'super_admin') {
        navigate('/admin/super');
      } else if (role === 'brand') {
        navigate('/dashboard/brand');
      } else {
        navigate('/dashboard/organizer');
      }
    } catch (error) {
      console.error('Demo login failed:', error);
      alert('Demo login failed. Please try again.');
    } finally {
      setLoading(null);
    }
  };
  const demoAccounts = [{
    role: 'brand' as const,
    title: 'Brand Demo',
    description: 'Experience the platform as a brand looking for sponsorship opportunities',
    icon: Building2,
    features: ['View and manage brand profile', 'Browse organizer matches', 'Create sponsorship offers', 'Track active partnerships', 'Access messaging system'],
    color: 'from-blue-500 to-blue-600'
  }, {
    role: 'organizer' as const,
    title: 'Organizer Demo',
    description: 'Explore the platform as an event organizer seeking brand partnerships',
    icon: Calendar,
    features: ['Manage event portfolio', 'Discover brand matches', 'Review sponsorship proposals', 'Track event metrics', 'Communicate with brands'],
    color: 'from-purple-500 to-purple-600'
  }, {
    role: 'super_admin' as const,
    title: 'Super Admin',
    description: 'Full administrative access to manage the entire platform',
    icon: Shield,
    features: ['Review all applications', 'Manage user accounts', 'View system analytics', 'Control matching algorithm', 'Access all data and reports'],
    color: 'from-red-500 to-red-600'
  }];
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" data-id="element-2242">
      <div className="container mx-auto px-4 py-12" data-id="element-2243">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-2244">
          <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-2245">
            Demo Access Portal
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-2246">
            Choose a role to explore the platform with pre-configured demo data
          </p>
        </div>

        {/* Demo Account Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto" data-id="element-2247">
          {demoAccounts.map(account => {
          const Icon = account.icon;
          const isLoading = loading === account.role;
          return <div key={account.role} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow" data-id="element-2248">
                {/* Card Header with Gradient */}
                <div className={`bg-gradient-to-r ${account.color} p-6 text-white`} data-id="element-2249">
                  <Icon className="w-12 h-12 mb-4" data-id="element-2250" />
                  <h2 className="text-2xl font-bold mb-2" data-id="element-2251">{account.title}</h2>
                  <p className="text-white/90 text-sm" data-id="element-2252">{account.description}</p>
                </div>

                {/* Features List */}
                <div className="p-6" data-id="element-2253">
                  <h3 className="font-semibold text-gray-900 mb-3" data-id="element-2254">
                    What you can do:
                  </h3>
                  <ul className="space-y-2 mb-6" data-id="element-2255">
                    {account.features.map((feature, index) => <li key={index} className="flex items-start text-sm text-gray-600" data-id="element-2256">
                        <span className="text-green-500 mr-2" data-id="element-2257">✓</span>
                        {feature}
                      </li>)}
                  </ul>

                  {/* Login Button */}
                  <Button onClick={() => handleDemoLogin(account.role)} disabled={loading !== null} className="w-full" data-id="element-2258">
                    {isLoading ? 'Logging in...' : `Login as ${account.title}`}
                  </Button>
                </div>
              </div>;
        })}
        </div>

        {/* Footer Note */}
        <div className="text-center mt-12" data-id="element-2259">
          <p className="text-sm text-gray-500" data-id="element-2260">
            Demo accounts are pre-configured with sample data for testing
            purposes
          </p>
          <button onClick={() => navigate('/login')} className="text-blue-600 hover:text-blue-700 text-sm mt-2 underline" data-id="element-2261">
            Back to regular login
          </button>
        </div>
      </div>
    </div>;
}